import { CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { ReportDataModel, ReportModel } from "../../models/report.model";
import { ChartDataSourceEnum, ChartTickesFormatEnum, BarDirectionEnum, Chart } from "../../models/company.model";
import { Bar } from "react-chartjs-2";
import { chartHeight, parseChartExpression, useGroupBy } from "../../chart-helpers/group.table.helper";

export default function CustomizedBar(props: { chart: Chart; reports?: ReportDataModel }) {
  const { groupBy } = props.chart || {};
  return (
    <>
      {!groupBy && <CommonBar chart={props.chart} reports={props.reports} />}
      {groupBy && <GroupByBar chart={props.chart} reports={props.reports} />}
    </>
  );
}

function GroupByBar(props: { chart: Chart; reports?: ReportDataModel }) {
  const { loading, groups, groupLabels } = useGroupBy({ ...props, isSingle: true });
  return (
    <CommonBar
      chart={props.chart}
      reports={{
        main: {} as ReportModel,
        ranges: groupLabels,
        dates: groups[0].map((g) => ({
          ...g,
          sk: g.groupLabel || "",
        })) as ReportModel[],
      }}
      loading={loading}
    />
  );
}

function CommonBar(props: { chart: Chart; reports?: ReportDataModel; loading?: boolean }) {
  const { label, data, bar } = props.chart || {};
  const dataSource = bar?.dataSource || ChartDataSourceEnum.Main;
  const chartLabels =
    dataSource === ChartDataSourceEnum.Main ? (data || []).map((d) => d.label) : props.reports?.ranges.map((r) => r) || [];
  const datasets =
    dataSource === ChartDataSourceEnum.Main
      ? [
          {
            labels: (data || []).map((d) => d.label),
            data: (data || []).map((d) => parseChartExpression(props.reports?.[dataSource], d.expression, d.decimalPlaces)),
            backgroundColor: (data || []).map((d) => d.color),
            borderRadius: 5,
          },
        ]
      : (data || []).map((d) => ({
          label: d.label,
          data: (chartLabels || []).map((label) =>
            parseChartExpression(
              props.reports?.[dataSource]?.find((d) => d.sk === label),
              d.expression,
              d.decimalPlaces
            )
          ),
          backgroundColor: d.color,
          borderRadius: 5,
        }));
  const tickesFormat = bar?.tickesFormat || ChartTickesFormatEnum.Decimal;
  const formatMax = tickesFormat === ChartTickesFormatEnum.Percent ? 1 : Math.max(...(datasets || []).map((d) => d.data).flat()) + 5;
  const direction = bar?.direction || BarDirectionEnum.Vertical;
  return (
    <Stack spacing={2}>
      <Paper elevation={2} className="padding-small">
        <Typography variant={"h6"} textAlign={"center"}>
          {label || "\u00A0"}
        </Typography>
      </Paper>
      <Paper elevation={2} className="padding-normal">
        <div style={{ height: chartHeight }}>
          {!props.loading && (
            <Bar
              plugins={[
                {
                  id: "overBar",
                  afterDatasetsDraw: (context) => {
                    const ctx = context.ctx;
                    context.data.datasets.forEach(function (dataset, datasetIndex) {
                      const meta = context.getDatasetMeta(datasetIndex);
                      let decimalPlaces = data[datasetIndex]?.decimalPlaces || 0
                      meta.data.forEach((item, i) => {
                        const text = (() => {
                          if (dataSource === ChartDataSourceEnum.Main) decimalPlaces = data[i]?.decimalPlaces || 0
                          const value = dataset.data[i] as number
                          if (!value) {
                            return "";
                          }
                          if (tickesFormat === ChartTickesFormatEnum.Percent) {
                            return `${(value * 100).toFixed(decimalPlaces)}%`;
                          }
                          if (tickesFormat === ChartTickesFormatEnum.Currency) {
                            return `$${value.toLocaleString("en-US", {
                              minimumFractionDigits: decimalPlaces || 2,
                              maximumFractionDigits: decimalPlaces || 2,
                            })}`;
                          }
                          return value.toFixed(decimalPlaces);
                        })();
                        ctx.textAlign = "center";
                        ctx.textBaseline = "middle";
                        ctx.fillStyle = "grey";
                        ctx.save();
                        if (direction === BarDirectionEnum.Vertical) {
                          ctx.translate(item.x, item.y - 10);
                        } else {
                          ctx.translate(item.x + 15 + (tickesFormat === ChartTickesFormatEnum.Currency ? 5 : 0), item.y);
                        }
                        ctx.fillText(String(text), 0, 0);
                        ctx.restore();
                      });
                    });
                  },
                },
              ]}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: direction === BarDirectionEnum.Vertical ? "x" : "y",
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label(tooltipItem) {
                        const index = dataSource === ChartDataSourceEnum.Main ? tooltipItem.dataIndex : tooltipItem.datasetIndex
                        const decimalPlaces =  data[index]?.decimalPlaces || 0
                        const formattedValue = typeof tooltipItem.raw === 'number' ? tooltipItem.raw.toFixed(decimalPlaces) : tooltipItem.formattedValue
                        return `${formattedValue}`
                      },
                    }
                  }
                },
                scales: {
                  x: {
                    grid: { display: false },
                    ...(direction === BarDirectionEnum.Horizontal && {
                      ticks: {
                        format: {
                          style: tickesFormat,
                          ...(tickesFormat === ChartTickesFormatEnum.Currency && {
                            currency: "USD",
                          }),
                        },
                      },
                      max: formatMax,
                    }),
                  },
                  y: {
                    grid: { display: false },
                    ...(direction === BarDirectionEnum.Vertical && {
                      ticks: {
                        format: {
                          style: tickesFormat,
                          ...(tickesFormat === ChartTickesFormatEnum.Currency && {
                            currency: "USD",
                          }),
                        },
                      },
                      max: formatMax,
                    }),
                  },
                },
              }}
              data={{
                labels: chartLabels,
                datasets,
              }}
              key={`${direction}${tickesFormat}${dataSource}`}
            />
          )}
          {props?.loading && (
            <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={4} height={"100%"}>
              <CircularProgress />
            </Stack>
          )}
        </div>
      </Paper>
    </Stack>
  );
}
